import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

// Create axios instance with default config
const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  // Demo login for testing
  demoLogin: async (email) => {
    const response = await api.post('/auth/demo-login', { email });
    return response.data;
  },
  // Local login with password
  localLogin: async (email, password) => {
    const response = await api.post('/auth/local-login', { email, password });
    return response.data;
  },
  // Azure AD login - exchange ID token for app token
  azureLogin: async (idToken, accessToken = null) => {
    const response = await api.post('/auth/azure/token', { 
      id_token: idToken,
      access_token: accessToken 
    });
    return response.data;
  },
  // Legacy login method (alias for demoLogin)
  login: async (email) => {
    const response = await api.post('/auth/demo-login', { email });
    return response.data;
  },
  getConfig: async () => {
    const response = await api.get('/auth/config');
    return response.data;
  },
  getMe: async () => {
    // Authorization: Bearer <token> is attached automatically by the request interceptor above
    const response = await api.get('/auth/me');
    return response.data;
  },
};

// Users API
export const usersAPI = {
  getAll: async (includeInactive = false) => {
    const response = await api.get(`/users/?include_inactive=${includeInactive}`);
    return response.data;
  },
  getPending: async () => {
    const response = await api.get('/users/pending');
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },
  assignPartners: async (userId, partnerIds) => {
    const response = await api.post(`/users/${userId}/assign-partners`, partnerIds);
    return response.data;
  },
  promoteToAdmin: async (userId) => {
    const response = await api.post(`/users/${userId}/promote-to-admin`);
    return response.data;
  },
  demoteToPM: async (userId) => {
    const response = await api.post(`/users/${userId}/demote-to-pm`);
    return response.data;
  },
  promoteToSuperAdmin: async (userId) => {
    const response = await api.post(`/users/${userId}/promote-to-superadmin`);
    return response.data;
  },
  approveUser: async (userId) => {
    const response = await api.post(`/users/${userId}/approve`);
    return response.data;
  },
  denyUser: async (userId) => {
    const response = await api.post(`/users/${userId}/deny`);
    return response.data;
  },
  toggleActive: async (userId) => {
    const response = await api.post(`/users/${userId}/toggle-active`);
    return response.data;
  },
  update: async (userId, data) => {
    const response = await api.patch(`/users/${userId}`, data);
    return response.data;
  },
  delete: async (userId) => {
    const response = await api.delete(`/users/${userId}`);
    return response.data;
  },
  permanentDelete: async (userId) => {
    const response = await api.delete(`/users/${userId}/permanent`);
    return response.data;
  },
};

// Partners API
export const partnersAPI = {
  getAll: async () => {
    const response = await api.get('/partners/');
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/partners/${id}`);
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/partners/', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.patch(`/partners/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/partners/${id}`);
    return response.data;
  },
};

// Training Requests API
export const trainingRequestsAPI = {
  getAll: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.partner_id) params.append('partner_id', filters.partner_id);
    if (filters.pm_id) params.append('pm_id', filters.pm_id);
    if (filters.status) params.append('status', filters.status);
    if (filters.project_type) params.append('project_type', filters.project_type);
    if (filters.limit) params.append('limit', filters.limit);
    const response = await api.get(`/training-requests/?${params}`);
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/training-requests/${id}`);
    return response.data;
  },
  create: async (data, pmId) => {
    const response = await api.post(`/training-requests/?pm_id=${pmId}`, data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.patch(`/training-requests/${id}`, data);
    return response.data;
  },
  updateStatus: async (id, status) => {
    const response = await api.patch(`/training-requests/${id}/status?status=${status}`);
    return response.data;
  },
  updateAttendance: async (id, registered, actual) => {
    const params = new URLSearchParams();
    if (registered !== undefined) params.append('registered', registered);
    if (actual !== undefined) params.append('actual', actual);
    const response = await api.patch(`/training-requests/${id}/attendance?${params}`);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/training-requests/${id}`);
    return response.data;
  },
};

// Courses API
export const coursesAPI = {
  getAll: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.is_custom !== undefined) params.append('is_custom', filters.is_custom);
    if (filters.solution_area) params.append('solution_area', filters.solution_area);
    if (filters.search) params.append('search', filters.search);
    const response = await api.get(`/courses/?${params}`);
    return response.data;
  },
  getPulseCatalog: async (params = {}) => {
    const query = new URLSearchParams();
    if (params.search) query.append('search', params.search);
    if (params.level && params.level !== 'all') query.append('level', params.level);
    if (params.technology && params.technology !== 'all') query.append('technology', params.technology);
    if (params.page) query.append('page', params.page);
    if (params.limit) query.append('limit', params.limit);
    const response = await api.get(`/courses/pulse/catalog?${query}`);
    return response.data;
  },
  getPulseFilters: async () => {
    const response = await api.get('/courses/pulse/technologies');
    return response.data;
  },
  refreshPulseCache: async () => {
    const response = await api.post('/courses/pulse/refresh');
    return response.data;
  },
  searchPulse: async (query) => {
    const response = await api.get(`/courses/pulse/search?query=${encodeURIComponent(query)}`);
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/courses/', data);
    return response.data;
  },
  createCustom: async (data) => {
    const response = await api.post('/courses/custom', data);
    return response.data;
  },
  delete: async (courseId) => {
    const response = await api.delete(`/courses/${courseId}`);
    return response.data;
  },
};

// TSP Quotations API
export const quotationsAPI = {
  getByRequest: async (trainingRequestId) => {
    const response = await api.get(`/tsp-quotations/?training_request_id=${trainingRequestId}`);
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/tsp-quotations/', data);
    return response.data;
  },
  select: async (id) => {
    const response = await api.patch(`/tsp-quotations/${id}/select`);
    return response.data;
  },
  compare: async (trainingRequestId) => {
    const response = await api.get(`/tsp-quotations/compare/${trainingRequestId}`);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/tsp-quotations/${id}`);
    return response.data;
  },
};

// Trainers API
export const trainersAPI = {
  getAll: async (filters = {}) => {
    const q = new URLSearchParams();
    if (filters.country) q.append('country', filters.country);
    if (filters.is_active !== undefined) q.append('is_active', filters.is_active);
    const response = await api.get(`/trainers/?${q}`);
    return response.data;
  },
  get: async (id) => {
    const response = await api.get(`/trainers/${id}`);
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/trainers/', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/trainers/${id}`, data);
    return response.data;
  },
  deactivate: async (id) => {
    const response = await api.delete(`/trainers/${id}`);
    return response.data;
  },
  getRecommendations: async (params = {}) => {
    const q = new URLSearchParams();
    if (params.course_id) q.append('course_id', params.course_id);
    if (params.course_title) q.append('course_title', params.course_title);
    if (params.solution_area) q.append('solution_area', params.solution_area);
    if (params.session_type) q.append('session_type', params.session_type);
    if (params.country) q.append('country', params.country);
    const response = await api.get(`/trainers/recommendations?${q}`);
    return response.data;
  },
  checkConflict: async (trainerId, params) => {
    const q = new URLSearchParams({
      session_date: params.session_date,
      start_time: params.start_time,
      end_time: params.end_time,
    });
    if (params.exclude_session_id) q.append('exclude_session_id', params.exclude_session_id);
    const response = await api.get(`/trainers/${trainerId}/check-conflict?${q}`);
    return response.data;
  },
  getSessions: async (trainerId) => {
    const response = await api.get(`/trainers/${trainerId}/sessions`);
    return response.data;
  },
  downloadCsvTemplate: () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    window.open(`${backendUrl}/api/trainers/csv-template`, '_blank');
  },
  bulkImportCsv: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/trainers/bulk-import-csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};


// Sessions API
export const sessionsAPI = {
  getAll: async () => {
    const response = await api.get('/sessions/');
    return response.data;
  },
  getByProject: async (trainingProjectId) => {
    const response = await api.get(`/sessions/?training_project_id=${trainingProjectId}`);
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/sessions/', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/sessions/${id}`, data);
    return response.data;
  },
  updateAttendance: async (id, registered, attended) => {
    const params = new URLSearchParams();
    if (registered !== undefined) params.append('registered', registered);
    if (attended !== undefined) params.append('attended', attended);
    const response = await api.patch(`/sessions/${id}/attendance?${params}`);
    return response.data;
  },
  updateStatus: async (id, status) => {
    const response = await api.patch(`/sessions/${id}/status?status=${status}`);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/sessions/${id}`);
    return response.data;
  },
  // Bulk import
  downloadTemplate: () => {
    // Return the URL for downloading the template
    return `${API}/sessions/bulk/template`;
  },
  bulkImport: async (trainingProjectId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/sessions/bulk/import/${trainingProjectId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  exportWithFeedback: (fmt = 'xlsx') => {
    window.open(`${API}/sessions/export?fmt=${fmt}`, '_blank');
  },
};

// Analytics API
export const analyticsAPI = {
  getDashboard: async (partnerIds = [], pmId = null, startDate = null, endDate = null, projectType = null, fiscalYear = null) => {
    const params = new URLSearchParams();
    partnerIds.forEach(id => params.append('partner_ids', id));
    if (pmId) params.append('pm_id', pmId);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (projectType) params.append('project_type', projectType);
    if (fiscalYear) params.append('fiscal_year', fiscalYear);
    const response = await api.get(`/analytics/dashboard?${params}`);
    return response.data;
  },
  getOpsOverview: async () => {
    const response = await api.get('/ops/overview');
    return response.data;
  },
  getTSPSummary: async (partnerIds = []) => {
    const params = new URLSearchParams();
    partnerIds.forEach(id => params.append('partner_ids', id));
    const response = await api.get(`/analytics/tsp-summary?${params}`);
    return response.data;
  },
  exportToExcel: async (partnerIds = [], pmId = null) => {
    const params = new URLSearchParams();
    partnerIds.forEach(id => params.append('partner_ids', id));
    if (pmId) params.append('pm_id', pmId);
    const response = await api.get(`/analytics/export?${params}`, {
      responseType: 'blob',
    });
    return response.data;
  },
};

// TSP (Training Service Providers) API
export const tspsAPI = {
  getAll: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.is_active !== undefined) params.append('is_active', filters.is_active);
    if (filters.is_preferred !== undefined) params.append('is_preferred', filters.is_preferred);
    const response = await api.get(`/tsps/?${params}`);
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/tsps/${id}`);
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/tsps/', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.patch(`/tsps/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/tsps/${id}`);
    return response.data;
  },
};

// Purchase Orders API
export const purchaseOrdersAPI = {
  getMonthlyConsumption: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.project_type) params.append('project_type', filters.project_type);
    const response = await api.get(`/purchase-orders/monthly-consumption?${params}`);
    return response.data;
  },
  getMismatchQueue: async () => {
    const response = await api.get('/purchase-orders/mismatch-queue');
    return response.data;
  },
  getInvoicesDueSoon: async () => {
    const response = await api.get('/purchase-orders/invoices-due-soon');
    return response.data;
  },
  getRecentCount: async () => {
    const response = await api.get('/purchase-orders/recent-count');
    return response.data;
  },
  getAll: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.tsp_id) params.append('tsp_id', filters.tsp_id);
    if (filters.status) params.append('status', filters.status);
    if (filters.active_only) params.append('active_only', 'true');
    if (filters.include_extensions) params.append('include_extensions', 'true');
    if (filters.limit) params.append('limit', filters.limit);
    const response = await api.get(`/purchase-orders/?${params}`);
    return response.data;
  },
  getForTsp: async (tspId) => {
    const response = await api.get(`/purchase-orders/for-tsp/${tspId}`);
    return response.data;
  },
  getAllSelectable: async () => {
    const response = await api.get('/purchase-orders/all-selectable');
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/purchase-orders/${id}`);
    return response.data;
  },
  create: async (data, createdBy = null) => {
    const params = createdBy ? `?created_by=${createdBy}` : '';
    const response = await api.post(`/purchase-orders/${params}`, data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.patch(`/purchase-orders/${id}`, data);
    return response.data;
  },
  allocate: async (poId, trainingProjectId, amount, notes = null) => {
    const params = new URLSearchParams();
    params.append('training_project_id', trainingProjectId);
    params.append('amount', amount);
    if (notes) params.append('notes', notes);
    const response = await api.post(`/purchase-orders/${poId}/allocate?${params}`);
    return response.data;
  },
  cancel: async (id) => {
    const response = await api.delete(`/purchase-orders/${id}`);
    return response.data;
  },
  deletePermanent: async (id, force = false) => {
    const response = await api.delete(`/purchase-orders/${id}/permanent${force ? '?force=true' : ''}`);
    return response.data;
  },
  bulkDelete: async (ids) => {
    const response = await api.post('/purchase-orders/bulk-delete', ids);
    return response.data;
  },
  getComplianceAlerts: async () => {
    const response = await api.get('/purchase-orders/compliance/alerts');
    return response.data;
  },
  getExtensions: async (poId) => {
    const response = await api.get(`/purchase-orders/${poId}/extensions`);
    return response.data;
  },
  addExtension: async (poId, data, createdBy = null) => {
    const params = createdBy ? `?created_by=${createdBy}` : '';
    const response = await api.post(`/purchase-orders/${poId}/extensions${params}`, data);
    return response.data;
  },
  deleteExtension: async (poId, extensionId) => {
    const response = await api.delete(`/purchase-orders/${poId}/extensions/${extensionId}`);
    return response.data;
  },
  exportExtensionHistory: async () => {
    const response = await api.get('/purchase-orders/extensions/export', {
      responseType: 'blob',
    });
    return response.data;
  },
  exportUtilisation: async () => {
    const response = await api.get('/purchase-orders/utilisation/export', {
      responseType: 'blob',
    });
    return response.data;
  },
};

// PO Documents API — shared upload + AI extraction for Delivery POs and Content POs
export const poDocumentsAPI = {
  extract: async (file, poType = 'delivery') => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/po-documents/extract?po_type=${poType}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  getFileUrl: (path) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    return `${base}/api/po-documents/file/${path}`;
  },
};

// Notifications API
export const notificationsAPI = {
  getAll: async (userId) => {
    const response = await api.get(`/notifications/?user_id=${encodeURIComponent(userId)}`);
    return response.data;
  },
  markRead: async (notificationKey, userId) => {
    const encodedKey = encodeURIComponent(notificationKey);
    const response = await api.post(`/notifications/read/${encodedKey}?user_id=${encodeURIComponent(userId)}`);
    return response.data;
  },
  markAllRead: async (userId) => {
    const response = await api.post(`/notifications/read-all?user_id=${encodeURIComponent(userId)}`);
    return response.data;
  },
};

// Feedback API
export const feedbackAPI = {
  // Session Feedback
  getSessionFeedback: async (sessionId) => {
    const response = await api.get(`/feedback/session/${sessionId}`);
    return response.data;
  },
  getAllSessionFeedback: async () => {
    const response = await api.get('/feedback/sessions/all');
    return response.data;
  },
  getAllSessionFeedbackForProject: async (projectId) => {
    const response = await api.get(`/feedback/sessions/project/${projectId}`);
    return response.data;
  },
  createSessionFeedback: async (data, submittedBy = null) => {
    const params = submittedBy ? `?submitted_by=${submittedBy}` : '';
    const response = await api.post(`/feedback/session${params}`, data);
    return response.data;
  },
  updateSessionFeedback: async (sessionId, data) => {
    const response = await api.patch(`/feedback/session/${sessionId}`, data);
    return response.data;
  },
  
  // Project Feedback
  getProjectFeedback: async (projectId) => {
    const response = await api.get(`/feedback/project/${projectId}`);
    return response.data;
  },
  createProjectFeedback: async (data, submittedBy = null) => {
    const params = submittedBy ? `?submitted_by=${submittedBy}` : '';
    const response = await api.post(`/feedback/project${params}`, data);
    return response.data;
  },
  updateProjectFeedback: async (projectId, data) => {
    const response = await api.patch(`/feedback/project/${projectId}`, data);
    return response.data;
  },
  
  // Completion Check
  canMarkComplete: async (projectId) => {
    const response = await api.get(`/feedback/project/${projectId}/can-complete`);
    return response.data;
  },
  
  // Summary
  getFeedbackSummary: async (projectId) => {
    const response = await api.get(`/feedback/summary/${projectId}`);
    return response.data;
  },
};

// Partner Conferences API
export const partnerConferencesAPI = {
  getAll: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.pm_id) params.append('pm_id', filters.pm_id);
    if (filters.status) params.append('status', filters.status);
    const response = await api.get(`/partner-conferences/?${params}`);
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/partner-conferences/${id}`);
    return response.data;
  },
  create: async (data, pmId) => {
    const response = await api.post(`/partner-conferences/?pm_id=${pmId}`, data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.patch(`/partner-conferences/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/partner-conferences/${id}`);
    return response.data;
  },
  copy: async (id, pmId) => {
    const response = await api.post(`/partner-conferences/${id}/copy?pm_id=${pmId}`);
    return response.data;
  },
  // Photos
  uploadPhoto: async (conferenceId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/partner-conferences/${conferenceId}/photos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  getPhotos: async (conferenceId) => {
    const response = await api.get(`/partner-conferences/${conferenceId}/photos`);
    return response.data;
  },
  deletePhoto: async (photoId) => {
    const response = await api.delete(`/partner-conferences/photo/${photoId}/delete`);
    return response.data;
  },
  getPhotoUrl: (photoId) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    return `${base}/api/partner-conferences/photo/${photoId}`;
  },
};

// Regional In-Person API
export const regionalInPersonAPI = {
  getAll: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.pm_id) params.append('pm_id', filters.pm_id);
    if (filters.region) params.append('region', filters.region);
    if (filters.delivery_status) params.append('delivery_status', filters.delivery_status);
    if (filters.workload) params.append('workload', filters.workload);
    const response = await api.get(`/regional-in-person/?${params}`);
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/regional-in-person/${id}`);
    return response.data;
  },
  create: async (data, pmId) => {
    const response = await api.post(`/regional-in-person/?pm_id=${pmId}`, data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.patch(`/regional-in-person/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/regional-in-person/${id}`);
    return response.data;
  },
  copy: async (id, pmId) => {
    const response = await api.post(`/regional-in-person/${id}/copy?pm_id=${pmId}`);
    return response.data;
  },
  getAnalytics: async () => {
    const response = await api.get('/regional-in-person/analytics');
    return response.data;
  },
  // Photos
  uploadPhoto: async (eventId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/regional-in-person/${eventId}/photos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  getPhotos: async (eventId) => {
    const response = await api.get(`/regional-in-person/${eventId}/photos`);
    return response.data;
  },
  deletePhoto: async (photoId) => {
    const response = await api.delete(`/regional-in-person/photo/${photoId}/delete`);
    return response.data;
  },
  getPhotoUrl: (photoId) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    return `${base}/api/regional-in-person/photo/${photoId}`;
  },
  // XLSX Bulk Import
  downloadTemplate: () => {
    const base = process.env.REACT_APP_BACKEND_URL;
    window.open(`${base}/api/regional-in-person/template`, '_blank');
  },
  bulkImport: async (file, pmId) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/regional-in-person/bulk-import?pm_id=${pmId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  // Report generation
  generateReport: async (id) => {
    const response = await api.post(`/reports/rio/${id}`);
    return response.data;
  },
};

// Reports API (shared)
export const reportsAPI = {
  generateRIOReport: async (id) => {
    const response = await api.post(`/reports/rio/${id}`);
    return response.data;
  },
  generatePrivateDeliveryReport: async (id) => {
    const response = await api.post(`/reports/private-delivery/${id}`);
    return response.data;
  },
  generatePPRWReport: async (id) => {
    const response = await api.post(`/reports/pprw/${id}`);
    return response.data;
  },
};

// PPRW API
export const pprwAPI = {
  getAll: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    const response = await api.get(`/pprw/?${params.toString()}`);
    return response.data;
  },
  get: async (id) => {
    const response = await api.get(`/pprw/${id}`);
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/pprw/', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.patch(`/pprw/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/pprw/${id}`);
    return response.data;
  },
  copy: async (id) => {
    const response = await api.post(`/pprw/${id}/copy`);
    return response.data;
  },
  bulkUpdate: async (session_ids, fields) => {
    const response = await api.patch('/pprw/bulk-update', { session_ids, fields });
    return response.data;
  },
  workshops: {
    getAll: async (filters = {}) => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const response = await api.get(`/pprw/workshops?${params.toString()}`);
      return response.data;
    },
    get: async (id) => {
      const response = await api.get(`/pprw/workshops/${id}`);
      return response.data;
    },
    create: async (data) => {
      const response = await api.post('/pprw/workshops', data);
      return response.data;
    },
    createFromSessions: async (session_ids, title) => {
      const response = await api.post('/pprw/workshops/from-sessions', { session_ids, title });
      return response.data;
    },
    update: async (id, data) => {
      const response = await api.patch(`/pprw/workshops/${id}`, data);
      return response.data;
    },
    delete: async (id) => {
      const response = await api.delete(`/pprw/workshops/${id}`);
      return response.data;
    },
    addSessions: async (id, session_ids) => {
      const response = await api.post(`/pprw/workshops/${id}/add-sessions`, { session_ids });
      return response.data;
    },
    removeSessions: async (id, session_ids) => {
      const response = await api.post(`/pprw/workshops/${id}/remove-sessions`, { session_ids });
      return response.data;
    },
    saveAsTemplate: async (id, name) => {
      const response = await api.post(`/pprw/workshops/${id}/save-as-template`, { name });
      return response.data;
    },
    templates: {
      getAll: async () => (await api.get('/pprw/workshop-templates')).data,
      create: async (data) => (await api.post('/pprw/workshop-templates', data)).data,
      delete: async (id) => (await api.delete(`/pprw/workshop-templates/${id}`)).data,
      update: async (id, data) => (await api.patch(`/pprw/workshop-templates/${id}`, data)).data,
      instantiate: async (id, body) => (await api.post(`/pprw/workshop-templates/${id}/instantiate`, body)).data,
    },
  },
  getAnalytics: async () => {
    const response = await api.get('/pprw/analytics');
    return response.data;
  },
  getTemplateUrl: () => `${API}/pprw/template`,
  getExportUrl: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    return `${API}/pprw/export?${params.toString()}`;
  },
  bulkImport: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/pprw/bulk-import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  getLabUsage: async () => {
    const response = await api.get('/pprw/lab-usage/');
    return response.data;
  },
  getLabUsageSummary: async () => {
    const response = await api.get('/pprw/lab-usage/summary');
    return response.data;
  },
  createLabUsage: async (data) => {
    const response = await api.post('/pprw/lab-usage/', data);
    return response.data;
  },
  updateLabUsage: async (id, data) => {
    const response = await api.patch(`/pprw/lab-usage/${id}`, data);
    return response.data;
  },
  deleteLabUsage: async (id) => {
    const response = await api.delete(`/pprw/lab-usage/${id}`);
    return response.data;
  },
};

// Service Requests API (inbound from Technofocus website + admin triage)
export const serviceRequestsAPI = {
  getAll: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.category_id) params.append('category_id', filters.category_id);
    if (filters.search) params.append('search', filters.search);
    const response = await api.get(`/service-requests/?${params.toString()}`);
    return response.data;
  },
  getStats: async () => {
    const response = await api.get('/service-requests/stats');
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/service-requests/${id}`);
    return response.data;
  },
  review: async (id, data) => {
    const response = await api.patch(`/service-requests/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/service-requests/${id}`);
    return response.data;
  },
};

// Content Development — Content POs API
export const contentPOsAPI = {
  getAll: async (statusOrFilters = null) => {
    const filters = typeof statusOrFilters === 'string' ? { status: statusOrFilters } : (statusOrFilters || {});
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.include_extensions) params.append('include_extensions', 'true');
    const response = await api.get(`/content-pos/?${params}`);
    return response.data;
  },
  getActive: async () => {
    const response = await api.get('/content-pos/active');
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/content-pos/${id}`);
    return response.data;
  },
  create: async (data, createdBy = null) => {
    const params = createdBy ? `?created_by=${createdBy}` : '';
    const response = await api.post(`/content-pos/${params}`, data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.patch(`/content-pos/${id}`, data);
    return response.data;
  },
  close: async (id) => {
    const response = await api.delete(`/content-pos/${id}`);
    return response.data;
  },
  deletePermanent: async (id, force = false) => {
    const response = await api.delete(`/content-pos/${id}/permanent${force ? '?force=true' : ''}`);
    return response.data;
  },
  getExtensions: async (poId) => {
    const response = await api.get(`/content-pos/${poId}/extensions`);
    return response.data;
  },
  addExtension: async (poId, data, createdBy = null) => {
    const params = createdBy ? `?created_by=${createdBy}` : '';
    const response = await api.post(`/content-pos/${poId}/extensions${params}`, data);
    return response.data;
  },
  deleteExtension: async (poId, extensionId) => {
    const response = await api.delete(`/content-pos/${poId}/extensions/${extensionId}`);
    return response.data;
  },
};

// Content Development — Content Items API
export const contentItemsAPI = {
  getAll: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    const response = await api.get(`/content-items/?${params}`);
    return response.data;
  },
  getStandalone: async () => {
    const response = await api.get('/content-items/standalone');
    return response.data;
  },
  getSimilarTitles: async (title) => {
    const response = await api.get('/content-items/similar-titles', { params: { title } });
    return response.data;
  },
  getDashboardStats: async () => {
    const response = await api.get('/content-items/dashboard/stats');
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/content-items/${id}`);
    return response.data;
  },
  create: async (data, createdBy = null) => {
    const params = createdBy ? `?created_by=${createdBy}` : '';
    const response = await api.post(`/content-items/${params}`, data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.patch(`/content-items/${id}`, data);
    return response.data;
  },
  updateActivity: async (id, data) => {
    const response = await api.patch(`/content-items/${id}/activity`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/content-items/${id}`);
    return response.data;
  },
  bulkUpdate: async (ids, field, value) => {
    const response = await api.post('/content-items/bulk-update', { ids, field, value });
    return response.data;
  },
  downloadTemplate: () => {
    const base = process.env.REACT_APP_BACKEND_URL;
    window.open(`${base}/api/content-items/template`, '_blank');
  },
  bulkImport: async (file, createdBy = null) => {
    const formData = new FormData();
    formData.append('file', file);
    const params = createdBy ? `?created_by=${createdBy}` : '';
    const response = await api.post(`/content-items/bulk-import${params}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  exportTracker: () => {
    const base = process.env.REACT_APP_BACKEND_URL;
    window.open(`${base}/api/content-items/export`, '_blank');
  },
  assignPlannerTask: async (itemId, data, graphToken) => {
    const response = await api.post(`/content-items/${itemId}/activities/planner-assign`, data, {
      headers: { 'X-Graph-Token': graphToken },
    });
    return response.data;
  },
  getDevelopersSummary: async () => {
    const response = await api.get('/content-items/developers/summary');
    return response.data;
  },
  applyWorkback: async (id) => {
    const response = await api.post(`/content-items/${id}/apply-workback`);
    return response.data;
  },
  updatePhaseMeta: async (id, data) => {
    const response = await api.patch(`/content-items/${id}/phase-meta`, data);
    return response.data;
  },
  raiseTriage: async (id, data) => {
    const response = await api.post(`/content-items/${id}/triage`, data);
    return response.data;
  },
  resolveTriage: async (id, entryId, data = {}) => {
    const response = await api.post(`/content-items/${id}/triage/${entryId}/resolve`, data);
    return response.data;
  },
  editTriage: async (id, entryId, data) => {
    const response = await api.patch(`/content-items/${id}/triage/${entryId}`, data);
    return response.data;
  },
  addProposedDelivery: async (id, data) => {
    const response = await api.post(`/content-items/${id}/proposed-deliveries`, data);
    return response.data;
  },
  removeProposedDelivery: async (id, entryId) => {
    const response = await api.delete(`/content-items/${id}/proposed-deliveries/${entryId}`);
    return response.data;
  },
  bulkResolveTriage: async (items, resolutionNote) => {
    const response = await api.post(`/content-items/triage/bulk-resolve`, { items, resolution_note: resolutionNote || null });
    return response.data;
  },
  getDeliveries: async (id) => {
    const response = await api.get(`/content-items/${id}/deliveries`);
    return response.data;
  },
  searchCatalog: async (q) => {
    const response = await api.get(`/content-items/catalog/search?q=${encodeURIComponent(q || '')}`);
    return response.data;
  },
  linkCatalog: async (id, catalogCourseId) => {
    const response = await api.post(`/content-items/${id}/link-catalog`, { catalog_course_id: catalogCourseId });
    return response.data;
  },
  refreshCatalog: async (id) => {
    const response = await api.post(`/content-items/${id}/refresh-catalog`);
    return response.data;
  },
  unlinkCatalog: async (id) => {
    const response = await api.post(`/content-items/${id}/unlink-catalog`);
    return response.data;
  },
  unlinkDelivery: async (id, source, sourceId) => {
    const response = await api.post(`/content-items/${id}/unlink-delivery`, { source, source_id: sourceId });
    return response.data;
  },
};

export const contentProjectsAPI = {
  getAll: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    const response = await api.get(`/content-projects/?${params}`);
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/content-projects/${id}`);
    return response.data;
  },
  create: async (data, createdBy = null) => {
    const params = createdBy ? `?created_by=${encodeURIComponent(createdBy)}` : '';
    const response = await api.post(`/content-projects/${params}`, data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.patch(`/content-projects/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/content-projects/${id}`);
    return response.data;
  },
};

// Global site search
export const searchAPI = {
  query: async (q) => {
    const response = await api.get('/search/', { params: { q } });
    return response.data;
  },
};

// Taxonomy — Super-Admin-managed master lists (Solution Areas, Readiness Stages, Asset Types, etc.)
export const taxonomyAPI = {
  getAll: async () => {
    const response = await api.get('/taxonomy/');
    return response.data;
  },
  getCategory: async (key) => {
    const response = await api.get(`/taxonomy/${key}`);
    return response.data;
  },
  updateCategory: async (key, items, updatedBy = null) => {
    const response = await api.put(`/taxonomy/${key}`, { items, updated_by: updatedBy });
    return response.data;
  },
  createCategory: async (label, items = [], updatedBy = null) => {
    const response = await api.post('/taxonomy/', { label, items, updated_by: updatedBy });
    return response.data;
  },
  deleteCategory: async (key) => {
    const response = await api.delete(`/taxonomy/${key}`);
    return response.data;
  },
  getUsage: async () => {
    const response = await api.get('/taxonomy/usage');
    return response.data;
  },
};

// Content Development — Workback Template API
export const workbackTemplateAPI = {
  get: async () => {
    const response = await api.get('/workback-template/');
    return response.data;
  },
  update: async (phases, updatedBy = null) => {
    const response = await api.put('/workback-template/', { phases, updated_by: updatedBy });
    return response.data;
  },
  list: async () => {
    const response = await api.get('/workback-template/list');
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/workback-template/create', data);
    return response.data;
  },
  updateTemplate: async (id, data) => {
    const response = await api.put(`/workback-template/${id}`, data);
    return response.data;
  },
  remove: async (id) => {
    const response = await api.delete(`/workback-template/${id}`);
    return response.data;
  },
  calculateGoLive: async (templateId, poReceiptDate) => {
    const response = await api.post('/workback-template/calculate-go-live', { template_id: templateId, po_receipt_date: poReceiptDate });
    return response.data;
  },
};

// Platform (LevelUp++) — Projects & Tasks tracker
export const platformWorkAPI = {
  getAll: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    const response = await api.get(`/platform-work/?${params.toString()}`);
    return response.data;
  },
  getStats: async () => {
    const response = await api.get('/platform-work/stats');
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/platform-work/${id}`);
    return response.data;
  },
  uploadImage: async (fileObj) => {
    const fd = new FormData();
    fd.append('file', fileObj);
    const response = await api.post('/platform-work/upload-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    return response.data;
  },
  generateKb: async (id) => {
    const response = await api.post(`/platform-work/${id}/generate-kb`);
    return response.data;
  },
  getKbArticles: async (search = '') => {
    const response = await api.get(`/platform-work/kb-articles${search ? `?search=${encodeURIComponent(search)}` : ''}`);
    return response.data;
  },
  create: async (data, createdBy = null) => {
    const params = createdBy ? `?created_by=${encodeURIComponent(createdBy)}` : '';
    const response = await api.post(`/platform-work/${params}`, data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.patch(`/platform-work/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/platform-work/${id}`);
    return response.data;
  },
  archive: async (id) => {
    const response = await api.post(`/platform-work/${id}/archive`);
    return response.data;
  },
  restore: async (id) => {
    const response = await api.post(`/platform-work/${id}/restore`);
    return response.data;
  },
  bulkArchive: async (ids) => {
    const response = await api.post('/platform-work/bulk/archive', { ids });
    return response.data;
  },
  bulkRestore: async (ids) => {
    const response = await api.post('/platform-work/bulk/restore', { ids });
    return response.data;
  },
  bulkDelete: async (ids) => {
    const response = await api.post('/platform-work/bulk/delete', { ids });
    return response.data;
  },
  getWorkback: async () => {
    const response = await api.get('/platform-work/workback');
    return response.data;
  },
  updateWorkback: async (stages, updatedBy = null) => {
    const response = await api.put('/platform-work/workback', { stages, updated_by: updatedBy });
    return response.data;
  },
};

// Schema-level custom taxonomy fields (Super-Admin attaches a taxonomy to an entity once)
export const customFieldsAPI = {
  listEntities: async () => {
    const response = await api.get('/custom-fields/entities');
    return response.data;
  },
  list: async (entityType) => {
    const response = await api.get(`/custom-fields/?entity_type=${encodeURIComponent(entityType)}`);
    return response.data;
  },
  attach: async (entityType, taxonomyId, multiSelect = false, createdBy = null) => {
    const response = await api.post('/custom-fields/', {
      entity_type: entityType, taxonomy_id: taxonomyId, multi_select: multiSelect, created_by: createdBy,
    });
    return response.data;
  },
  update: async (fieldId, data) => {
    const response = await api.patch(`/custom-fields/${fieldId}`, data);
    return response.data;
  },
  detach: async (fieldId) => {
    const response = await api.delete(`/custom-fields/${fieldId}`);
    return response.data;
  },
  getValues: async (entityType, recordId) => {
    const response = await api.get(`/custom-fields/values/${entityType}/${recordId}`);
    return response.data;
  },
  setValues: async (entityType, recordId, values) => {
    const response = await api.put(`/custom-fields/values/${entityType}/${recordId}`, { values });
    return response.data;
  },
};

export const preferencesAPI = {
  get: async () => {
    const response = await api.get('/users/me/preferences');
    return response.data;
  },
  set: async (key, value) => {
    const response = await api.put('/users/me/preferences', { key, value });
    return response.data;
  },
};

export const myTasksAPI = {
  get: async () => {
    const response = await api.get('/my-tasks');
    return response.data;
  },
};

export default api;
